import {
  CartAdd,
  cartAdd,
  printMoney,
  getCurrentCart,
  getCountOfVariantInCart,
  ON_CART_FINISHED,
} from '@process-creative/slate-theme-tools';

import { Swatches } from '../../objects/product/swatches';
import { Product } from '../../tools/product/product';
import { ATTR_DISABLED } from '../../tools/constants/attributes';
import {
  EVT_VARIANT_CHANGED,
  UpdatedVariant,
} from '../../objects/product/variant-selector';
import {
  CLASS_INITIALISED,
  CLASS_DISABLED,
  CLASS_VISIBLE,
} from '../../tools/constants/classes';
import { t } from '../../tools/utilities/string-tools';
import { checkFreeGifts } from '../cart/cart-free-gifts';
// Selectors
const SEL_FORM_CONTAINER = '[data-product]';
const SEL_ADD = '[data-product-form-add]';
const SEL_FORM = '[data-product-form-form]';
const SEL_MONEY = '[data-product-money]';
const SEL_COMPARE = '[data-product-compare-money]';
const SELECTOR_CART_COUNT = '[data-cart-count]';
const SELECTOR_PREORDER_MESSAGE = '[data-preorder-message]';
const SELECTOR_AFTERPAY_PAYMENT = '[data-afterpay-price]';

// Attributes
const ATTR_STICKY_ATC = 'data-sticky-add-to-cart-button';

export class ProductForm {
  container: HTMLElement;
  btnAdd: HTMLButtonElement | null = null;
  form: HTMLFormElement | null = null;
  preorderMessage: HTMLElement | null = null;
  swatches: Swatches | null = null;
  afterpayPayment: HTMLSpanElement | null = null;
  private money: NodeListOf<HTMLSpanElement> | null = null;
  private compare: NodeListOf<HTMLSpanElement> | null = null;

  private adding = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.queryChildren(container);
    this.attachEvents();
  }

  queryChildren(container: HTMLElement) {
    this.btnAdd = container.querySelector(SEL_ADD);
    this.form = container.querySelector(SEL_FORM);
    this.money = container.querySelectorAll<HTMLSpanElement>(SEL_MONEY);
    this.compare = container.querySelectorAll<HTMLSpanElement>(SEL_COMPARE);
    this.preorderMessage = container.querySelector<HTMLElement>(
      SELECTOR_PREORDER_MESSAGE
    );
    this.afterpayPayment = container.querySelector<HTMLElement>(
      SELECTOR_AFTERPAY_PAYMENT
    );
  }

  onFormSubmit = async (evt: Event) => {
    if (!this.form || !this.swatches) return;
    evt.preventDefault();
    const vrnt = this.swatches.getVariant();
    let { inventory_quantity, preorder_message } = vrnt;
    const { inventory_policy } = vrnt;
    const title = this.swatches.data.title;
    const bundle = this.swatches.data.tags.some((tag) =>
      tag.toLowerCase().includes('bundle')
    );
    let bundleProperty = '';

    if (bundle && title.includes('(')) {
      bundleProperty = title.split('(')[1].replace(')', '');
    } else if (bundle) {
      bundleProperty = 'bundle';
    }

    if (!preorder_message) preorder_message = t('products.product.preorder');

    if (!inventory_quantity) inventory_quantity = 0;

    // Check inventory policy, if continue && inventory qty <= 0
    const preorderProduct =
      inventory_quantity <= 0 && inventory_policy === 'continue';

    this.adding = true;
    if (this.btnAdd) {
      this.btnAdd.disabled = true;
      this.btnAdd.innerHTML = t('products.product.adding');
    }

    const item = Array.from(new FormData(this.form)).reduce((x, y) => {
      return { ...x, [y[0]]: y[1].toString() };
    }, {} as CartAdd);

    if (preorderProduct) {
      item.properties = {
        preorder: preorder_message,
      };
    }

    if (bundle && item.properties) {
      item.properties.bundle = bundleProperty;
    } else if (bundle) {
      item.properties = {
        bundle: bundleProperty,
      };
    }
    // Actually add to cart.
    try {
      item.quantity = 1;
      await cartAdd({ items: [item] });
      await checkFreeGifts(item.id);
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-alert
      alert(err);
    }

    // Update header cart
    this.updateHeaderCart();
    // Trigger onCartFinished event to activate the slide-out cart
    document.dispatchEvent(new Event(ON_CART_FINISHED));

    if (this.btnAdd) {
      this.btnAdd.disabled = false;

      if (!this.swatches) return;
      const variant = this.swatches.getVariant();
      const { price, inventory_management, inventory_policy, compare_at_price } = variant;
      let { inventory_quantity } = variant;
      if (!inventory_quantity) inventory_quantity = 0;
      const inCart = getCountOfVariantInCart(variant.id);

      const showPrice = this.btnAdd.hasAttribute(ATTR_STICKY_ATC) || this.btnAdd.hasAttribute('data-product-add-show-price');
      const savingsButton = document.querySelector('[data-product-form-add-savings-text]');

      let innerHTML = showPrice
        ? `${printMoney(price)} - ${compare_at_price ? `<s>${printMoney(compare_at_price)}</s>` : ''} ${t('products.product.add_to_cart')}`
        : `${t('products.product.add_to_cart')}`;

      if (savingsButton) {
        innerHTML = savingsButton.getAttribute('data-product-form-add-savings-text');
      }

      // Check if all available of variant is in the cart
      if (
        inventory_policy !== 'continue' &&
        inventory_management != null &&
        inventory_quantity - inCart <= 0
      ) {
        this.btnAdd.innerHTML = t('products.product.all_in_cart');
      } else if (inventory_quantity <= 0 && inventory_policy === 'continue') {
        this.btnAdd.innerHTML = t('products.product.preorder');
      } else {
        this.btnAdd.innerHTML = innerHTML;
      }
    }
    this.adding = false;
  };

  onVariantChange() {
    if (!this.swatches) return;
    const variant = this.swatches.getVariant();
    const { price, compare_at_price } = variant;

    this.updatePriceElements(price, compare_at_price);
    this.updateAddToCart(variant);
    this.updatePreorderMessage(variant);
    this.updateAfterpayPayment(variant);
  }

  updateAfterpayPayment(variant: UpdatedVariant) {
    const quarterPrice = variant.price / 4;
    if (this.afterpayPayment)
      this.afterpayPayment.innerHTML = printMoney(quarterPrice);
  }

  updatePreorderMessage(variant: UpdatedVariant) {
    if (!this.preorderMessage) return;
    const { inventory_quantity, inventory_policy, preorder_message } = variant;

    // checking typeof because quantity being 0 is falsy
    if (!inventory_policy || typeof inventory_quantity !== 'number') return;

    // Check inventory policy, if continue && inventory qty <= 0
    const preorderProduct =
      inventory_quantity <= 0 && inventory_policy === 'continue';

    if (preorderProduct) {
      if (preorder_message) {
        this.preorderMessage.classList.add(CLASS_VISIBLE);
        this.preorderMessage.innerHTML = preorder_message;
      } else {
        this.preorderMessage.classList.remove(CLASS_VISIBLE);
      }
    } else {
      this.preorderMessage.classList.remove(CLASS_VISIBLE);
    }
  }

  /**
   * Updates elements displaying price based on selected variant
   * @param price variant price
   * @param comparePrice variant compare at price
   */
  updatePriceElements(price: number, comparePrice?: number | null) {
    if (this.money) {
      this.money.forEach((el) => (el.innerText = printMoney(price)));
    }
    if (comparePrice && this.compare) {
      this.compare.forEach((el) => (el.innerText = printMoney(comparePrice)));
    }
    if (!comparePrice && this.compare) {
      this.compare.forEach((el) => (el.innerText = ''));
    }
  }

  /**
   * Updates Add to cart button label based on selected variant
   * @param variant - variant object
   */
  updateAddToCart = async (variant: UpdatedVariant) => {
    if (!this.btnAdd) return;

    const showPrice = this.btnAdd.hasAttribute(ATTR_STICKY_ATC) || this.btnAdd.hasAttribute('data-product-add-show-price');
    // Adjust the add to cart button based on QTY available and in cart
    const inCart = getCountOfVariantInCart(variant.id);
    const { available, inventory_policy, inventory_management, price, compare_at_price } =
      variant;
    let { inventory_quantity } = variant;
    if (!inventory_quantity) inventory_quantity = 0;

    if (available) {
      if (
        inventory_policy !== 'continue' &&
        inventory_management != null &&
        inventory_quantity - inCart <= 0
      ) {
        this.btnAllInCart();
      } else if (inventory_policy === 'continue' && inventory_quantity < 1) {
        this.btnEnable(showPrice, price, compare_at_price, true);
      } else {
        this.btnEnable(showPrice, price, compare_at_price);
      }
    } else {
      this.btnDisable();
    }
  };

  /**
   * Updates button label if all items are in the cart
   */
  btnAllInCart = () => {
    if (!this.btnAdd) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAdd.innerHTML = t('products.product.all_in_cart');
  };

  /**
   * Disables ATC if out of stock
   */
  btnDisable = () => {
    if (!this.btnAdd) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAdd.innerHTML = t('products.product.sold_out');
  };

  /**
   * Enables button and updates label
   * @param price - price of selected variant
   * @param includePrice - boolean, if true shows price in button
   */
  btnEnable = (includePrice: boolean, price: number, comparePrice: number, preorder = false) => {
    if (!this.btnAdd) return;
    this.btnAdd.classList.remove(CLASS_DISABLED);
    this.btnAdd.removeAttribute(ATTR_DISABLED);
    const savingsButton = document.querySelector('[data-product-form-add-savings-text]');

    if (preorder) {
      this.btnAdd.innerHTML = t('products.product.preorder');
    } else if (savingsButton) {
      this.btnAdd.innerHTML = `${savingsButton.getAttribute('data-product-form-add-savings-text')}`;
    } else if (includePrice) {
      this.btnAdd.innerHTML = `${printMoney(price)} ${comparePrice ? `<s>${printMoney(comparePrice)}</s>` : ''} - ${t(
        'products.product.add_to_cart'
      )}`;
    } else {
      this.btnAdd.innerHTML = t('products.product.add_to_cart');
    }
  };

  /**
   * Updates cart count in header
   */
  updateHeaderCart = () => {
    const countElement =
      document.querySelector<HTMLElement>(SELECTOR_CART_COUNT);
    if (!countElement) return;

    const count = getCurrentCart().item_count;

    countElement.innerHTML = `${count}`;

    if (count > 0) {
      countElement.classList.add(CLASS_VISIBLE);
    } else {
      countElement.classList.remove(CLASS_VISIBLE);
    }
  };

  attachEvents() {
    this.swatches = new Swatches(this.container, true);
    this.form?.addEventListener(EVT_VARIANT_CHANGED, () =>
      this.onVariantChange()
    );
    this.form?.addEventListener('submit', this.onFormSubmit);
  }

  detatchEvents() {
    this.swatches?.dispose();
    this.form?.removeEventListener('submit', this.onFormSubmit);
    this.form?.removeEventListener(EVT_VARIANT_CHANGED, () =>
      this.onVariantChange()
    );
  }
}

document.querySelectorAll<HTMLElement>(SEL_FORM_CONTAINER).forEach((form) => {
  if (form.classList.contains(CLASS_INITIALISED)) return;
  form.classList.add(CLASS_INITIALISED);

  // What the hell?
  // eslint-disable-next-line no-new
  new ProductForm(form);
});
